/**
 * 오버레이 사이드바 컴포넌트
 * 모바일 및 태블릿에서 사용되는 오버레이 형태의 사이드바
 */

import React, { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
    BarChart3,
    Users,
    CreditCard,
    LogOut,
    Settings,
    X
} from 'lucide-react';
import { colors, typography, spacing, borderRadius, shadows, animations, gradients, zIndex } from '../../styles/designTokens';

// ===== 타입 정의 =====
export interface OverlaySidebarProps {
    isOpen: boolean;
    onClose: () => void;
    adminInfo?: {
        admin_name?: string;
        admin_id?: string;
    } | null;
    onLogout?: () => void;
}

export interface SidebarMenuItem {
    id: string;
    label: string;
    icon: React.ComponentType<any>;
    path: string;
    badge?: string | number;
}

// ===== 스타일드 컴포넌트 =====
const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: ${zIndex.overlayBackdrop};
  opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
  visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
`;

const SidebarContainer = styled.aside<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 320px;
  height: 100vh;
  background: linear-gradient(180deg, ${colors.semantic.background.primary} 0%, ${colors.semantic.background.secondary} 100%);
  border-right: 1px solid ${colors.semantic.border.primary};
  display: flex;
  flex-direction: column;
  z-index: ${zIndex.overlayPanel};
  transform: translateX(${({ $isOpen }) => $isOpen ? '0' : '-100%'});
  transition: transform ${animations.duration.normal} ${animations.easing.smooth};
  box-shadow: ${shadows['2xl']};
  overflow: hidden;
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${colors.semantic.border.secondary};
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.semantic.text.tertiary};
  }
`;

const SidebarHeader = styled.div`
  padding: ${spacing[6]} ${spacing[4]};
  border-bottom: 1px solid ${colors.semantic.border.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 80px;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
`;

const LogoImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  border-radius: ${borderRadius.md};
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
`;

const LogoText = styled.div`
  h1 {
    font-size: ${typography.fontSize.lg};
    font-weight: ${typography.fontWeight.bold};
    color: ${colors.semantic.text.primary};
    margin: 0;
    background: ${gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  p {
    font-size: ${typography.fontSize.xs};
    color: ${colors.semantic.text.tertiary};
    margin: 0;
    font-weight: ${typography.fontWeight.medium};
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.md};
  background: ${colors.semantic.background.primary};
  color: ${colors.semantic.text.secondary};
  cursor: pointer;
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  
  &:hover {
    background: ${colors.semantic.background.tertiary};
    border-color: ${colors.primary[300]};
    color: ${colors.primary[600]};
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: ${spacing[4]} 0;
`;

const MenuSection = styled.div`
  margin-bottom: ${spacing[6]};
`;

const MenuTitle = styled.h3`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 ${spacing[3]} 0;
  padding: 0 ${spacing[4]};
`;

const MenuList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const MenuItem = styled.li`
  margin: 0 ${spacing[2]};
  margin-bottom: ${spacing[1]};
`;

const MenuLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borderRadius.lg};
  text-decoration: none;
  color: ${colors.semantic.text.secondary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  position: relative;
  overflow: hidden;
  
  /* 호버 효과 */
  &:hover {
    background: ${colors.primary[50]};
    color: ${colors.primary[700]};
    transform: translateX(4px);
    
    svg {
      color: ${colors.primary[600]};
      transform: scale(1.1);
    }
  }
  
  /* 활성 상태 */
  &.active {
    background: linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%);
    color: ${colors.semantic.text.inverse};
    box-shadow: ${shadows.primary};
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: ${colors.semantic.text.inverse};
      border-radius: 0 ${borderRadius.sm} ${borderRadius.sm} 0;
    }
    
    svg {
      color: ${colors.semantic.text.inverse};
    }
  }
  
  /* 아이콘 스타일 */
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    transition: all ${animations.duration.fast} ${animations.easing.smooth};
  }
  
  /* 텍스트 스타일 */
  .menu-text {
    white-space: nowrap;
    overflow: hidden;
  }
  
  /* 배지 스타일 */
  .menu-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 ${spacing[2]};
    background: ${colors.error[500]};
    color: ${colors.semantic.text.inverse};
    font-size: ${typography.fontSize.xs};
    font-weight: ${typography.fontWeight.bold};
    border-radius: ${borderRadius.full};
    margin-left: auto;
  }
  
  /* 모션 감소 */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
    
    &:hover {
      transform: none !important;
    }
    
    svg {
      transform: none !important;
    }
  }
`;

const UserSection = styled.div`
  margin-top: auto;
  padding: ${spacing[4]};
  border-top: 1px solid ${colors.semantic.border.primary};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[3]};
  background: linear-gradient(135deg, ${colors.primary[50]} 0%, ${colors.primary[100]} 100%);
  border-radius: ${borderRadius.lg};
  border: 1px solid ${colors.primary[200]};
  margin-bottom: ${spacing[3]};
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  
  &:hover {
    background: linear-gradient(135deg, ${colors.primary[100]} 0%, ${colors.primary[200]} 100%);
    transform: translateY(-1px);
    box-shadow: ${shadows.md};
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.semantic.text.inverse};
  font-weight: ${typography.fontWeight.bold};
  font-size: ${typography.fontSize.sm};
  flex-shrink: 0;
`;

const UserDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.primary};
  margin-bottom: ${spacing[1]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${colors.semantic.text.tertiary};
  font-weight: ${typography.fontWeight.medium};
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  border: 1px solid ${colors.error[200]};
  border-radius: ${borderRadius.lg};
  background: ${colors.semantic.background.primary};
  color: ${colors.error[600]};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  
  &:hover {
    background: ${colors.error[50]};
    border-color: ${colors.error[300]};
    color: ${colors.error[700]};
    transform: translateY(-1px);
    box-shadow: ${shadows.error};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
  
  /* 모션 감소 */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
    
    &:hover {
      transform: none !important;
    }
  }
`;

// ===== 메뉴 데이터 =====
const menuItems: SidebarMenuItem[] = [
    { id: 'dashboard', label: '대시보드', icon: BarChart3, path: '/dashboard' },
    { id: 'customers', label: '고객 관리', icon: Users, path: '/customers' },
    { id: 'payments', label: '결제 관리', icon: CreditCard, path: '/payments' },
    { id: 'settings', label: '설정', icon: Settings, path: '/settings' },
];

// ===== 메인 컴포넌트 =====
export const OverlaySidebar: React.FC<OverlaySidebarProps> = ({
    isOpen,
    onClose,
    adminInfo,
    onLogout
}) => {
    const location = useLocation();

    // ESC 키로 닫기
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            // 스크롤 방지
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    const handleLogout = () => {
        if (onLogout) {
            onLogout();
        }
        onClose();
    };

    return (
        <>
            <Overlay $isOpen={isOpen} onClick={onClose} />
            <SidebarContainer $isOpen={isOpen}>
                {/* 헤더 */}
                <SidebarHeader>
                    <LogoContainer>
                        <LogoImage
                            src="/images/logo.png"
                            alt="BRANDINSIGHT Logo"
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                        <LogoText>
                            <h1>BI Admin</h1>
                            <p>관리 시스템</p>
                        </LogoText>
                    </LogoContainer>

                    <CloseButton onClick={onClose} aria-label="사이드바 닫기">
                        <X />
                    </CloseButton>
                </SidebarHeader>

                {/* 메뉴 */}
                <SidebarContent>
                    <MenuSection>
                        <MenuTitle>메인 메뉴</MenuTitle>
                        <MenuList>
                            {menuItems.map((item) => {
                                const IconComponent = item.icon;
                                const isActive = location.pathname === item.path ||
                                    (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

                                return (
                                    <MenuItem key={item.id}>
                                        <MenuLink
                                            to={item.path}
                                            className={isActive ? 'active' : ''}
                                            onClick={onClose}
                                        >
                                            <IconComponent />
                                            <span className="menu-text">{item.label}</span>
                                            {item.badge && (
                                                <span className="menu-badge">{item.badge}</span>
                                            )}
                                        </MenuLink>
                                    </MenuItem>
                                );
                            })}
                        </MenuList>
                    </MenuSection>
                </SidebarContent>

                {/* 사용자 정보 및 로그아웃 */}
                <UserSection>
                    {adminInfo && (
                        <UserInfo>
                            <UserAvatar>
                                {adminInfo.admin_name?.charAt(0) || 'A'}
                            </UserAvatar>
                            <UserDetails>
                                <UserName>{adminInfo.admin_name || '관리자'}</UserName>
                                <UserRole>{adminInfo.admin_id || 'admin'}</UserRole>
                            </UserDetails>
                        </UserInfo>
                    )}

                    <LogoutButton onClick={handleLogout}>
                        <LogOut />
                        로그아웃
                    </LogoutButton>
                </UserSection>
            </SidebarContainer>
        </>
    );
};

export default OverlaySidebar;
